import React, { useState, useMemo, useRef } from "react";

import styled from "styled-components";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";

import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CreateOutlinedIcon from "@material-ui/icons/CreateOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";

import StyledIconButton from "app/Chat/buttons/StyledIconButton";

import OpenAISettings from "./OpenAISettings";

import { keyBy } from "./lib/utils";

const isDevEnv = process.env.NODE_ENV === "development";
// const isDevEnv = false;

const ChatSettings = (props) => {
  const {
    apiKey,
    baseChatModel,
    models,
    useMatchingTokenModel,
    useAnotherModelForFunctionCall,
    baseChatModelForFunctionCall,
    modelVariants,
    onApiKeyDelete,
    onApiKeySubmit,
    onSettingsSubmit,
    setInputProxyInStorage,
    inputProxyFromLocalStorage,
    useInputProxy,
  } = props;

  const [currentSettings, setCurrentSettings] = useState("");
  const [localSettings, setLocalSettings] = useState({
    baseChatModel,
    useMatchingTokenModel,
    baseChatModelForFunctionCall,
    useAnotherModelForFunctionCall,
    useInputProxy,
  });
  const [localApiKey, setLocalApiKey] = useState("");
  const [inputProxy, setInputProxy] = useState(
    inputProxyFromLocalStorage.length > 0 ? inputProxyFromLocalStorage : ""
  );

  const [errorMessage, setErrorMessage] = useState("");

  const apiKeyInputRef = useRef();

  const maskedKey = useMemo(() => {
    const first = apiKey.substring(0, 1);
    const mask = "*".repeat(11);

    return `${first}${mask}`;
  }, [apiKey]);

  const modelsById = useMemo(() => {
    return keyBy(models, (model) => model.id);
  }, [models]);

  const handleApiKeyChange = (e) => {
    setLocalApiKey(e.target.value);
  };

  const showApiKeyForm = () => {
    setCurrentSettings("apiKey");
  };

  const hideApiKeyForm = () => {
    setCurrentSettings("");
  };

  const handleApiKeySubmit = async (e) => {
    try {
      setErrorMessage("");
      e.preventDefault();
      e.stopPropagation();

      const key = localApiKey.trim();
      if (!key) {
        apiKeyInputRef.current?.focus();
        return;
      }

      await onApiKeySubmit(key);
      hideApiKeyForm();
    } catch (e) {
      console.log(`e->`, e);
      setErrorMessage(e.message);
    }
  };

  const handleProxyChange = (e) => {
    setInputProxy(e.target.value);
  };

  const handleProxyInputSubmit = async (e) => {
    try {
      e.preventDefault();
      e.stopPropagation();

      const key = inputProxy.trim();
      if (!key) {
        apiKeyInputRef.current?.focus();
        return;
      }

      await setInputProxyInStorage("inputProxy", key);
    } catch (e) {
      console.log(`e->`, e);
    }
  };

  const deleteApiKey = () => {
    onApiKeyDelete();
  };

  const editModel = () => {
    setCurrentSettings("model");
  };

  const handleSettingsChange = (newSettings) => {
    setLocalSettings((prevSettings) => {
      const _settings = { ...prevSettings, ...newSettings };
      onSettingsSubmit(_settings);
      return { ...prevSettings, ...newSettings };
    });
  };

  const handleModelChange = (e, newValue) => {
    handleSettingsChange({ baseChatModel: newValue });
  };

  const handleFnModelChange = (e, newValue) => {
    handleSettingsChange({ baseChatModelForFunctionCall: newValue });
  };

  const handleChangeUseMatchingTokenModel = (e) => {
    handleSettingsChange({ useMatchingTokenModel: e.target.checked });
  };

  const handleChangeModelForFunctionCall = (e) => {
    const isChecked = Boolean(e.target.checked);
    handleSettingsChange({
      useAnotherModelForFunctionCall: isChecked,
      baseChatModelForFunctionCall: null,
    });
  };

  const toggleProxy = (e) => {
    const isChecked = Boolean(e.target.checked);
    handleSettingsChange({
      useInputProxy: isChecked,
    });
  };

  const renderApiKeyForm = () => {
    return (
      <APIKeyForm onSubmit={handleApiKeySubmit}>
        <TextField
          variant="outlined"
          placeholder="Enter your Open AI API key"
          autoFocus
          ref={apiKeyInputRef.current}
          value={localApiKey}
          onChange={handleApiKeyChange}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
        />
        <StyledIconButton size="small" onClick={hideApiKeyForm}>
          <CloseOutlinedIcon />
        </StyledIconButton>
      </APIKeyForm>
    );
  };

  const renderModelSelectorForm = () => {
    return (
      <Autocomplete
        id={"openai-model"}
        fullWidth
        options={modelVariants}
        value={localSettings.baseChatModel}
        onChange={handleModelChange}
        groupBy={(option) => modelsById[option.model].name}
        getOptionLabel={(option) =>
          `${option.name} (${option.maxTokens} max tokens)`
        }
        getOptionDisabled={(option) => option.legacy}
        getOptionSelected={(option) =>
          option.name === localSettings.baseChatModel?.name
        }
        renderInput={(params) => (
          <TextField {...params} label="Model" variant="outlined" />
        )}
      />
    );
  };

  const renderApiKeyDetails = () => {
    return (
      <SettingsItemContainer>
        <SettingsItemHeading>Local OpenAI API Key</SettingsItemHeading>
        <SettingsItem>
          {currentSettings === "apiKey" ? (
            renderApiKeyForm()
          ) : apiKey ? (
            <>
              <p style={{ margin: 0 }}>{maskedKey}</p>
              <div>
                <StyledIconButton onClick={deleteApiKey}>
                  <DeleteOutlineIcon />
                </StyledIconButton>
              </div>
            </>
          ) : (
            <>
              <p style={{ margin: 0 }}>No API key provided</p>
              <Button
                onClick={showApiKeyForm}
                startIcon={<CreateOutlinedIcon />}
              >
                Add the key
              </Button>
            </>
          )}
        </SettingsItem>
      </SettingsItemContainer>
    );
  };

  const renderModelDetails = () => {
    return (
      <SettingsItemContainer>
        <SettingsItemHeading>OpenAI Chat model</SettingsItemHeading>
        <SettingsItem>
          {currentSettings === "model" ? (
            renderModelSelectorForm()
          ) : (
            <>
              <strong>
                {localSettings.baseChatModel?.name} (
                {localSettings.baseChatModel?.maxTokens} max tokens)
              </strong>
              <div>
                <StyledIconButton onClick={editModel}>
                  <EditOutlinedIcon />
                </StyledIconButton>
              </div>
            </>
          )}
        </SettingsItem>
      </SettingsItemContainer>
    );
  };

  const renderFnChatModelForm = () => {
    return (
      <div style={{ padding: "0.5rem 1.5rem" }}>
        <Autocomplete
          id={"fn-openai-model"}
          fullWidth
          options={modelVariants}
          value={localSettings.baseChatModelForFunctionCall}
          onChange={handleFnModelChange}
          groupBy={(option) => modelsById[option.model].name}
          getOptionLabel={(option) =>
            `${option.name} (${option.maxTokens} max tokens)`
          }
          getOptionDisabled={(option) => option.legacy}
          getOptionSelected={(option) =>
            option.name === localSettings.baseChatModelForFunctionCall?.name
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Function call model"
              variant="outlined"
            />
          )}
        />
      </div>
    );
  };

  const chatBehaviorDetails = () => {
    return (
      <SettingsItemContainer>
        <SettingsItemHeading>Chat Behavior</SettingsItemHeading>
        <SettingsItem>
          <div>
            <FormControl component="fieldset">
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={localSettings.useMatchingTokenModel}
                    name="useMatchingTokenModel"
                    onChange={handleChangeUseMatchingTokenModel}
                  />
                }
                label="Automatically switch to model (variant), which can handle the tokens size"
              />
            </FormControl>
            <FormControl component="fieldset">
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={Boolean(
                      localSettings.useAnotherModelForFunctionCall
                    )}
                    name="useAnotherModelForFunctionCall"
                    onChange={handleChangeModelForFunctionCall}
                  />
                }
                label="Automatically switch model for function call"
              />
              {localSettings.useAnotherModelForFunctionCall
                ? renderFnChatModelForm()
                : null}
            </FormControl>
          </div>
        </SettingsItem>
      </SettingsItemContainer>
    );
  };

  const renderTunnelingDetails = () => {
    return (
      <SettingsItemContainer>
        <SettingsItemHeading>Proxy for chat</SettingsItemHeading>
        <SettingsItem>
          <DefaultForm onSubmit={handleProxyInputSubmit}>
            <TextField
              variant="outlined"
              value={inputProxy}
              onChange={handleProxyChange}
            />
          </DefaultForm>
          <div>
            Use this proxy
            <Checkbox
              checked={Boolean(localSettings.useInputProxy)}
              onChange={toggleProxy}
              color="primary"
              name="changeProxyLink"
              label="Change proxy link"
            />
          </div>
        </SettingsItem>
      </SettingsItemContainer>
    );
  };

  const renderChatSettingsContent = () => {
    return (
      <>
        <OpenAISettings />
        {/* Render below settings only in local development environment */}
        {isDevEnv && renderApiKeyDetails()}
        {isDevEnv && renderModelDetails()}
        {isDevEnv && chatBehaviorDetails()}
        {isDevEnv && renderTunnelingDetails()}
      </>
    );
  };

  const renderChatSettings = () => {
    return <SettingsContent>{renderChatSettingsContent()}</SettingsContent>;
  };

  return renderChatSettings();
};

export default ChatSettings;

const SettingsContent = styled.div`
  && {
    padding: 0.5rem 1rem;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 10px;
    }

    &::-webkit-scrollbar-track {
      display: none;
    }

    &::-webkit-scrollbar-thumb {
      background-color: transparent;
      border-radius: 4px;
    }

    &:hover {
      &::-webkit-scrollbar-thumb {
        background-color: lightgray;
      }
    }
  }
`;

const SettingsItemContainer = styled.div`
  padding: 1rem 0;
`;

const SettingsItem = styled.div`
  padding: 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SettingsItemHeading = styled.h4`
  margin: 0;
  font-size: 12px;
  font-weight: normal;
  color: #818181;
`;

const APIKeyForm = styled.form`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DefaultForm = styled.form`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
